import React, { useEffect, useState } from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
import { ChevronDown } from 'lucide-react';
interface ExerciseLibraryProps {
  onBack: () => void;
  onSelect: (exercise: any) => void;
  onNext: () => void;
  selectedExercises: any[];
}
export const ExerciseLibrary = ({
  onBack,
  onSelect,
  onNext,
  selectedExercises
}: ExerciseLibraryProps) => {
  const [activeCategory, setActiveCategory] = useState('upperPush');
  const [activeSlot, setActiveSlot] = useState({
    part: 'A',
    index: 0
  });
  // Workout structure with slots for Part A and Part B
  const [workoutSlots, setWorkoutSlots] = useState({
    A: [{
      type: 'upperPush',
      exercise: null
    }, {
      type: 'lowerPull',
      exercise: null
    }],
    B: [{
      type: 'lowerPush',
      exercise: null
    }, {
      type: 'upperPull',
      exercise: null
    }, {
      type: 'cardio',
      exercise: null
    }]
  });
  // Map selected exercises to slots
  useEffect(() => {
    // Create a copy of the workout slots
    const updatedSlots = {
      ...workoutSlots
    };
    // Clear all assigned exercises
    Object.keys(updatedSlots).forEach(part => {
      updatedSlots[part].forEach((slot, index) => {
        updatedSlots[part][index].exercise = null;
      });
    });
    // Assign selected exercises to slots
    selectedExercises.forEach(exercise => {
      if (exercise.slotPart && exercise.slotIndex !== undefined) {
        const {
          slotPart,
          slotIndex
        } = exercise;
        if (updatedSlots[slotPart] && updatedSlots[slotPart][slotIndex]) {
          updatedSlots[slotPart][slotIndex].exercise = exercise;
        }
      }
    });
    setWorkoutSlots(updatedSlots);
  }, [selectedExercises]);
  // Make sure activeCategory always reflects the active slot's type
  useEffect(() => {
    if (activeSlot.part && activeSlot.index !== undefined) {
      const slotType = workoutSlots[activeSlot.part][activeSlot.index]?.type;
      if (slotType) {
        setActiveCategory(slotType);
      }
    }
  }, [activeSlot, workoutSlots]);
  const categories = [{
    id: 'upperPush',
    name: 'Upper Push'
  }, {
    id: 'upperPull',
    name: 'Upper Pull'
  }, {
    id: 'lowerPush',
    name: 'Lower Push'
  }, {
    id: 'lowerPull',
    name: 'Lower Pull'
  }, {
    id: 'core',
    name: 'Core'
  }, {
    id: 'cardio',
    name: 'Cardio'
  }];
  const exercises = {
    upperPush: [{
      id: 'bench',
      name: 'Bench Press',
      muscle: 'Chest',
      equipmentOptions: ['barbell', 'dumbbell'],
      lastSession: {
        weight: 185,
        date: '2025-08-27',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'dumbbell_bench',
      name: 'Dumbbell Bench Press',
      muscle: 'Chest',
      equipmentOptions: ['dumbbell']
    }, {
      id: 'incline_bench',
      name: 'Incline Bench Press',
      muscle: 'Upper Chest',
      equipmentOptions: ['barbell', 'dumbbell'],
      lastSession: {
        weight: 165,
        date: '2025-08-25',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'pushup',
      name: 'Push-up',
      muscle: 'Chest'
    }, {
      id: 'dips',
      name: 'Dips',
      muscle: 'Chest/Triceps'
    }, {
      id: 'shoulder',
      name: 'Shoulder Press',
      muscle: 'Shoulders',
      equipmentOptions: ['barbell', 'dumbbell'],
      lastSession: {
        weight: 135,
        date: '2025-08-20',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'lateral_raise',
      name: 'Lateral Raise',
      muscle: 'Shoulders',
      equipmentOptions: ['dumbbell']
    }, {
      id: 'tricep_extension',
      name: 'Triceps Extension',
      muscle: 'Triceps',
      equipmentOptions: ['dumbbell', 'cable']
    }, {
      id: 'tricep_pushdown',
      name: 'Triceps Pushdown',
      muscle: 'Triceps',
      equipmentOptions: ['cable']
    }],
    upperPull: [{
      id: 'pullup',
      name: 'Pull-up',
      muscle: 'Back',
      noWeight: true
    }, {
      id: 'chinup',
      name: 'Chin-up',
      muscle: 'Back/Biceps',
      noWeight: true
    }, {
      id: 'lat',
      name: 'Lat Pulldown',
      muscle: 'Back',
      equipmentOptions: ['cable']
    }, {
      id: 'row',
      name: 'Bent Over Row',
      muscle: 'Back',
      equipmentOptions: ['barbell', 'dumbbell'],
      lastSession: {
        weight: 155,
        date: '2025-08-22',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'dumbbell_row',
      name: 'Dumbbell Row',
      muscle: 'Back',
      equipmentOptions: ['dumbbell']
    }, {
      id: 'face_pull',
      name: 'Face Pull',
      muscle: 'Rear Delts',
      equipmentOptions: ['cable']
    }, {
      id: 'bicep',
      name: 'Bicep Curl',
      muscle: 'Arms',
      equipmentOptions: ['barbell', 'dumbbell', 'cable'],
      lastSession: {
        weight: 95,
        date: '2025-08-22',
        equipment: 'barbell',
        barWeight: 35
      }
    }, {
      id: 'hammer_curl',
      name: 'Hammer Curl',
      muscle: 'Biceps/Forearms',
      equipmentOptions: ['dumbbell']
    }],
    lowerPush: [{
      id: 'squat',
      name: 'Squat',
      muscle: 'Quads',
      equipmentOptions: ['barbell'],
      lastSession: {
        weight: 225,
        date: '2025-08-26',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'front_squat',
      name: 'Front Squat',
      muscle: 'Quads',
      equipmentOptions: ['barbell'],
      lastSession: {
        weight: 185,
        date: '2025-08-24',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'legpress',
      name: 'Leg Press',
      muscle: 'Quads',
      equipmentOptions: ['machine']
    }, {
      id: 'lunge',
      name: 'Lunges',
      muscle: 'Quads',
      equipmentOptions: ['barbell', 'dumbbell']
    }, {
      id: 'split_squat',
      name: 'Bulgarian Split Squat',
      muscle: 'Quads/Glutes',
      equipmentOptions: ['dumbbell']
    }, {
      id: 'leg_extension',
      name: 'Leg Extension',
      muscle: 'Quads',
      equipmentOptions: ['machine']
    }],
    lowerPull: [{
      id: 'deadlift',
      name: 'Deadlift',
      muscle: 'Hamstrings/Back',
      equipmentOptions: ['barbell'],
      lastSession: {
        weight: 275,
        date: '2025-08-23',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'rdl',
      name: 'Romanian Deadlift',
      muscle: 'Hamstrings/Glutes',
      equipmentOptions: ['barbell', 'dumbbell'],
      lastSession: {
        weight: 225,
        date: '2025-08-21',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'good_morning',
      name: 'Good Morning',
      muscle: 'Hamstrings/Lower Back',
      equipmentOptions: ['barbell']
    }, {
      id: 'leg_curl',
      name: 'Leg Curl',
      muscle: 'Hamstrings',
      equipmentOptions: ['machine']
    }, {
      id: 'glute_bridge',
      name: 'Glute Bridge',
      muscle: 'Glutes',
      equipmentOptions: ['barbell', 'bodyweight']
    }, {
      id: 'hip_thrust',
      name: 'Hip Thrust',
      muscle: 'Glutes',
      equipmentOptions: ['barbell'],
      lastSession: {
        weight: 205,
        date: '2025-08-19',
        equipment: 'barbell',
        barWeight: 45
      }
    }, {
      id: 'calfr',
      name: 'Calf Raises',
      muscle: 'Calves',
      equipmentOptions: ['machine', 'dumbbell']
    }],
    core: [{
      id: 'crunch',
      name: 'Crunches',
      muscle: 'Abs'
    }, {
      id: 'situp',
      name: 'Sit-ups',
      muscle: 'Abs'
    }, {
      id: 'plank',
      name: 'Plank',
      muscle: 'Core',
      noWeight: true
    }, {
      id: 'side_plank',
      name: 'Side Plank',
      muscle: 'Obliques',
      noWeight: true
    }, {
      id: 'russian',
      name: 'Russian Twist',
      muscle: 'Obliques'
    }, {
      id: 'legr',
      name: 'Leg Raises',
      muscle: 'Lower Abs'
    }, {
      id: 'mountain_climber',
      name: 'Mountain Climbers',
      muscle: 'Full Core',
      noWeight: true
    }],
    cardio: [{
      id: 'treadmill',
      name: 'Treadmill',
      muscle: 'Cardio',
      noWeight: true,
      isCardio: true
    }, {
      id: 'bike',
      name: 'Exercise Bike',
      muscle: 'Cardio',
      noWeight: true,
      isCardio: true
    }, {
      id: 'elliptical',
      name: 'Elliptical',
      muscle: 'Cardio',
      noWeight: true,
      isCardio: true
    }, {
      id: 'rower',
      name: 'Rowing Machine',
      muscle: 'Full Body',
      noWeight: true,
      isCardio: true
    }, {
      id: 'stairmaster',
      name: 'Stairmaster',
      muscle: 'Cardio/Legs',
      noWeight: true,
      isCardio: true
    }, {
      id: 'jump_rope',
      name: 'Jump Rope',
      muscle: 'Cardio',
      noWeight: true,
      isCardio: true
    }]
  };
  // Get category name from id
  const getCategoryName = categoryId => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };
  // Handle slot selection
  const handleSlotSelect = (part, index) => {
    setActiveSlot({
      part,
      index
    });
    // The activeCategory will be updated via the useEffect
  };
  // Handle exercise selection for a specific slot
  const handleExerciseSelect = exercise => {
    const {
      part,
      index
    } = activeSlot;
    // Check if this exercise is already selected in this slot
    const isAlreadySelected = selectedExercises.some(ex => ex.id === exercise.id && ex.slotPart === part && ex.slotIndex === index);
    if (isAlreadySelected) {
      // Remove from selection
      onSelect({
        ...exercise,
        slotPart: part,
        slotIndex: index
      });
    } else {
      // Add to selection with slot information
      const enhancedExercise = {
        ...exercise,
        slotPart: part,
        slotIndex: index,
        trackingType: exercise.isCardio ? 'calories' : undefined
      };
      onSelect(enhancedExercise);
    }
  };
  // Change the exercise type for a slot
  const handleSlotTypeChange = (part, index, newType) => {
    const updatedSlots = {
      ...workoutSlots
    };
    updatedSlots[part][index].type = newType;
    setWorkoutSlots(updatedSlots);
    // If this is the active slot, also update the active category immediately
    if (activeSlot.part === part && activeSlot.index === index) {
      setActiveCategory(newType);
    }
  };
  // Add a new slot to a part
  const addSlot = part => {
    const updatedSlots = {
      ...workoutSlots
    };
    // Determine default type based on part
    let defaultType = part === 'A' ? 'upperPush' : 'lowerPush';
    updatedSlots[part].push({
      type: defaultType,
      exercise: null
    });
    setWorkoutSlots(updatedSlots);
  };
  // Check if an exercise is selected for the current active slot
  const isSelectedForActiveSlot = exerciseId => {
    const {
      part,
      index
    } = activeSlot;
    return selectedExercises.some(ex => ex.id === exerciseId && ex.slotPart === part && ex.slotIndex === index);
  };
  // Get exercise name from slot
  const getExerciseNameFromSlot = (part, index) => {
    const slot = workoutSlots[part][index];
    if (slot.exercise) {
      return slot.exercise.name;
    }
    return null;
  };
  return <div className="flex flex-col h-full" data-id="element-46">
      <div className="p-6" data-id="element-47">
        <button onClick={onBack} className="text-gray-500 mb-4" data-id="element-48">
          ← Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-49">
          Exercise Library
        </h1>
        <p className="text-gray-600 mb-4" data-id="element-50">Select exercises for your workout</p>

        {/* Workout structure display */}
        <div className="mb-6 border rounded-xl p-4 bg-gray-50" data-id="element-51">
          <h2 className="font-bold text-gray-800 mb-4" data-id="element-52">Workout Structure</h2>
          {/* Part A */}
          <div className="mb-5" data-id="element-53">
            <h3 className="font-semibold text-gray-700 mb-3" data-id="element-54">Part A</h3>
            <div className="space-y-3" data-id="element-55">
              {workoutSlots.A.map((slot, index) => <div key={`A-${index}`} className={`flex items-center space-x-3 ${activeSlot.part === 'A' && activeSlot.index === index ? 'opacity-100' : 'opacity-80'}`} data-id="element-56">
                  <div className={`border-2 ${activeSlot.part === 'A' && activeSlot.index === index ? 'border-blue-500' : 'border-dashed border-gray-400'} rounded-lg p-3 flex-1 cursor-pointer hover:border-blue-400 transition-colors`} onClick={() => handleSlotSelect('A', index)} data-id="element-57">
                    {getExerciseNameFromSlot('A', index) ? <div className="font-medium text-gray-800" data-id="element-58">
                        {getExerciseNameFromSlot('A', index)}
                      </div> : <div className="text-gray-500" data-id="element-59">
                        [ exercise {index + 1} ]
                      </div>}
                  </div>
                  <div className="relative w-40" data-id="element-60">
                    <select value={slot.type} onChange={e => handleSlotTypeChange('A', index, e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg appearance-none pr-8 bg-white text-sm" data-id="element-61">
                      {categories.map(category => <option key={category.id} value={category.id} data-id="element-62">
                          {category.name}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-63">
                      <ChevronDown className="w-4 h-4" data-id="element-64" />
                    </div>
                  </div>
                </div>)}
              <button onClick={() => addSlot('A')} className="flex items-center text-blue-500 font-medium text-sm mt-2 hover:text-blue-600" data-id="element-65">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1" data-id="element-66">
                  <path d="M12 5v14M5 12h14" data-id="element-67"></path>
                </svg>
                add another
              </button>
            </div>
          </div>
          {/* Part B */}
          <div data-id="element-68">
            <h3 className="font-semibold text-gray-700 mb-3" data-id="element-69">Part B</h3>
            <div className="space-y-3" data-id="element-70">
              {workoutSlots.B.map((slot, index) => <div key={`B-${index}`} className={`flex items-center space-x-3 ${activeSlot.part === 'B' && activeSlot.index === index ? 'opacity-100' : 'opacity-80'}`} data-id="element-71">
                  <div className={`border-2 ${activeSlot.part === 'B' && activeSlot.index === index ? 'border-blue-500' : 'border-dashed border-gray-400'} rounded-lg p-3 flex-1 cursor-pointer hover:border-blue-400 transition-colors`} onClick={() => handleSlotSelect('B', index)} data-id="element-72">
                    {getExerciseNameFromSlot('B', index) ? <div className="font-medium text-gray-800" data-id="element-73">
                        {getExerciseNameFromSlot('B', index)}
                      </div> : <div className="text-gray-500" data-id="element-74">
                        [ exercise {index + 1} ]
                      </div>}
                  </div>
                  <div className="relative w-40" data-id="element-75">
                    <select value={slot.type} onChange={e => handleSlotTypeChange('B', index, e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg appearance-none pr-8 bg-white text-sm" data-id="element-76">
                      {categories.map(category => <option key={category.id} value={category.id} data-id="element-77">
                          {category.name}
                        </option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-78">
                      <ChevronDown className="w-4 h-4" data-id="element-79" />
                    </div>
                  </div>
                </div>)}
              <button onClick={() => addSlot('B')} className="flex items-center text-blue-500 font-medium text-sm mt-2 hover:text-blue-600" data-id="element-80">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1" data-id="element-81">
                  <path d="M12 5v14M5 12h14" data-id="element-82"></path>
                </svg>
                add another
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4" data-id="element-83">
          <h2 className="font-semibold text-gray-800" data-id="element-84">
            Select {getCategoryName(activeCategory)} for{' '}
            <span className="text-blue-500" data-id="element-85">
              Part {activeSlot.part}, Exercise {activeSlot.index + 1}
            </span>
          </h2>
        </div>

        <div className="flex overflow-x-auto space-x-2 py-2 mb-4" data-id="element-86">
          {categories.map(category => <button key={category.id} onClick={() => {
          setActiveCategory(category.id);
          // Also update the slot type
          handleSlotTypeChange(activeSlot.part, activeSlot.index, category.id);
        }} className={`px-4 py-2 rounded-full whitespace-nowrap ${activeCategory === category.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-87">
              {category.name}
            </button>)}
        </div>
      </div>

      <div className="flex-1 px-6 pb-6 overflow-y-auto" data-id="element-88">
        <div className="space-y-3" data-id="element-89">
          {exercises[activeCategory].map(exercise => <Card key={exercise.id} onClick={() => handleExerciseSelect(exercise)} className={`p-4 ${isSelectedForActiveSlot(exercise.id) ? 'border-blue-500 bg-blue-50' : ''}`} data-id="element-90">
              <div className="flex justify-between items-center" data-id="element-91">
                <div data-id="element-92">
                  <h3 className="font-medium" data-id="element-93">{exercise.name}</h3>
                  <p className="text-gray-500 text-sm" data-id="element-94">
                    {exercise.muscle}
                    {exercise.noWeight && ' • No weight needed'}
                    {exercise.isCardio && ' • Track calories or time'}
                    {exercise.equipmentOptions && ` • ${exercise.equipmentOptions.join(', ')}`}
                  </p>
                  {exercise.lastSession && <p className="text-blue-500 text-xs mt-1" data-id="element-95">
                      Last: {exercise.lastSession.weight}lb on{' '}
                      {new Date(exercise.lastSession.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
                    </p>}
                </div>
                {isSelectedForActiveSlot(exercise.id) ? <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-96">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-97">
                      <path d="M20 6 9 17l-5-5" data-id="element-98"></path>
                    </svg>
                  </div> : <div className="w-6 h-6 border border-gray-300 rounded-full" data-id="element-99"></div>}
              </div>
            </Card>)}
        </div>
      </div>

      <div className="p-4 border-t border-gray-100" data-id="element-100">
        <Button onClick={onNext} variant="primary" fullWidth disabled={selectedExercises.length === 0} className={selectedExercises.length === 0 ? 'opacity-50 cursor-not-allowed' : ''} data-id="element-101">
          Continue with {selectedExercises.length} exercise
          {selectedExercises.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>;
};